/**
 * QualityPostureDashboard — Phase 2 page
 * ─────────────────────────────────────────────────────────────────────────────
 * Rolling-window dashboard backed by GET /api/v1/bale-selection/posture-summary.
 * Shows the QC team how the laydown algorithm is performing over the last
 * N days: verdict mix (GOOD/WARNING/BAD), drift mix, per-recipe breakdown,
 * and the 20 most recent runs.
 *
 * Data flow:
 *   plant select → posture-summary fetch → 4 sections render in parallel
 *   - KPI strip (total runs, total bales, avg Mic CV, avg SCI CV)
 *   - Verdict tiles (3 colored cards using VerdictBadge palette)
 *   - Per-recipe table (sortable; bullseye-pill verdict mix)
 *   - Recent runs table (link → /bale-mgmt/laydown-planning?run_id=...)
 *
 * Spec: blueprint Sec 22 (operator-facing dashboard) + slide 22 verdict mix.
 * Used by: QC team for shift-handover review; sidebar entry under Bale Mgmt.
 */
import { useState } from 'react'
import {
  Alert, Card, Col, Empty, Row, Select, Space, Statistic, Table, Tag,
  Tooltip, Typography,
} from 'antd'
import {
  CheckCircleFilled, WarningFilled, CloseCircleFilled,
  RiseOutlined, ReloadOutlined,
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import PageHeader from '@/shared/components/PageHeader'
import VerdictBadge from '@/shared/components/VerdictBadge'
import { baleSelectionApi } from '@/features/baleManagement/api'
import { plantsApi } from '@/features/masters/plants/api'

const { Text } = Typography

// ── Types mirroring backend PostureSummaryResponse ───────────────────────────
type Verdict = 'GOOD' | 'WARNING' | 'BAD'

type VerdictDist = { GOOD: number; WARNING: number; BAD: number }
type DriftDist = { WITHIN_LIMITS: number; EXCEEDS_LIMITS: number; NO_PRIOR: number; UNKNOWN: number }

type RecipeBucket = {
  recipe_id: string
  recipe_name: string
  recipe_count_ne: number | null
  runs_count: number
  verdict_distribution: VerdictDist
  avg_mic_cv_pct: number | null
  avg_sci_cv_pct: number | null
  last_run_at: string | null
}

type RecentRun = {
  run_id: string
  created_at: string
  yarn_recipe_id: string
  within_band: string | null
  drift_status: string | null
  verdict: Verdict
  bale_count: number
  target_bales: number
}

type PostureSummary = {
  plant_id: string
  days: number
  overall: {
    total_runs: number
    total_bales_selected: number
    verdict_distribution: VerdictDist
    drift_distribution: DriftDist
    avg_mic_cv_pct: number | null
    avg_sci_cv_pct: number | null
  }
  by_recipe: RecipeBucket[]
  recent_runs: RecentRun[]
}

type Plant = { id: string; name: string }

// ── Verdict styling (mirrors VerdictBadge) ───────────────────────────────────
const VERDICT_STYLE: Record<Verdict, { color: string; bg: string; icon: React.ReactNode; label: string }> = {
  GOOD:    { color: '#52c41a', bg: '#f6ffed', icon: <CheckCircleFilled />, label: 'GOOD' },
  WARNING: { color: '#faad14', bg: '#fffbe6', icon: <WarningFilled />,     label: 'WARNING' },
  BAD:     { color: '#ff4d4f', bg: '#fff2f0', icon: <CloseCircleFilled />, label: 'NOT OK' },
}

const DRIFT_TAG_COLOR: Record<string, string> = {
  WITHIN_LIMITS: 'green',
  EXCEEDS_LIMITS: 'red',
  NO_PRIOR: 'default',
  UNKNOWN: 'default',
}

// ── Inline verdict-mix bar (no chart lib) ────────────────────────────────────
function VerdictMixBar({ dist }: { dist: VerdictDist }) {
  const total = dist.GOOD + dist.WARNING + dist.BAD
  if (total === 0) return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>
  const pct = (n: number) => (n / total) * 100
  return (
    <Tooltip title={`GOOD ${dist.GOOD} · WARNING ${dist.WARNING} · NOT OK ${dist.BAD}`}>
      <div style={{
        display: 'flex', height: 14, borderRadius: 4, overflow: 'hidden',
        border: '1px solid #f0f0f0', minWidth: 120,
      }}>
        {dist.GOOD > 0 && (
          <div style={{ width: `${pct(dist.GOOD)}%`, background: '#52c41a' }} />
        )}
        {dist.WARNING > 0 && (
          <div style={{ width: `${pct(dist.WARNING)}%`, background: '#faad14' }} />
        )}
        {dist.BAD > 0 && (
          <div style={{ width: `${pct(dist.BAD)}%`, background: '#ff4d4f' }} />
        )}
      </div>
    </Tooltip>
  )
}

// ── Big verdict tile (one of 3 in the top row) ───────────────────────────────
function VerdictTile({ verdict, count, total }: { verdict: Verdict; count: number; total: number }) {
  const m = VERDICT_STYLE[verdict]
  const pct = total > 0 ? Math.round((count / total) * 100) : 0
  return (
    <Card
      style={{
        background: m.bg, borderColor: m.color, borderWidth: 1.5,
        textAlign: 'center', height: '100%',
      }}
      styles={{ body: { padding: 20 } }}
    >
      <div style={{ fontSize: 36, color: m.color, lineHeight: 1 }}>{m.icon}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: m.color, marginTop: 6 }}>
        {m.label}
      </div>
      <div style={{ fontSize: 32, fontWeight: 700, marginTop: 4 }}>{count}</div>
      <Text type="secondary" style={{ fontSize: 12 }}>{pct}% of laydowns</Text>
    </Card>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function QualityPostureDashboard() {
  const [plantId, setPlantId] = useState<string | undefined>()
  const [days, setDays] = useState<number>(30)
  const [onlyApproved, setOnlyApproved] = useState<boolean>(true)

  const plantsQ = useQuery<Plant[]>({
    queryKey: ['plants', 'all'],
    queryFn: async () => {
      const res = await plantsApi.list()
      // API may return PaginatedResponse<Plant> ({items}/{data}) or a bare array.
      const r = res as unknown as { items?: Plant[]; data?: Plant[] } | Plant[]
      if (Array.isArray(r)) return r
      return r.items ?? r.data ?? []
    },
  })

  // Default to first plant on first render
  if (!plantId && plantsQ.data && plantsQ.data.length > 0) {
    setPlantId(plantsQ.data[0].id)
  }

  const summaryQ = useQuery<PostureSummary>({
    queryKey: ['posture-summary', plantId, days, onlyApproved],
    queryFn: () => baleSelectionApi.postureSummary({
      plant_id: plantId!,
      days,
      only_approved: onlyApproved,
    }),
    enabled: Boolean(plantId),
    refetchOnWindowFocus: false,
  })

  const summary = summaryQ.data

  return (
    <div style={{ padding: 16 }}>
      <PageHeader
        title="Quality Posture"
        subtitle="Rolling-window verdict + drift performance for bale-selection runs"
        extra={
          <Space>
            <Select
              value={plantId}
              onChange={setPlantId}
              placeholder="Plant"
              style={{ width: 180 }}
              loading={plantsQ.isLoading}
              options={(plantsQ.data || []).map(p => ({
                label: p.name, value: p.id,
              }))}
            />
            <Select
              value={days}
              onChange={setDays}
              style={{ width: 120 }}
              options={[
                { label: 'Last 7 days', value: 7 },
                { label: 'Last 30 days', value: 30 },
                { label: 'Last 90 days', value: 90 },
                { label: 'Last 180 days', value: 180 },
              ]}
            />
            <Select
              value={onlyApproved ? 'approved' : 'all'}
              onChange={(v) => setOnlyApproved(v === 'approved')}
              style={{ width: 180 }}
              options={[
                { label: 'Approved laydowns only', value: 'approved' },
                { label: 'Include drafts', value: 'all' },
              ]}
            />
            <Tooltip title="Refresh">
              <ReloadOutlined
                onClick={() => summaryQ.refetch()}
                style={{ fontSize: 16, cursor: 'pointer' }}
                spin={summaryQ.isFetching}
              />
            </Tooltip>
          </Space>
        }
      />

      {!plantId && (
        <Alert message="Pick a plant to begin." type="info" showIcon style={{ marginTop: 16 }} />
      )}

      {summaryQ.isError && (
        <Alert
          message="Failed to load posture summary"
          description={(summaryQ.error as Error)?.message}
          type="error" showIcon style={{ marginTop: 16 }}
        />
      )}

      {summary && (
        <>
          {/* ── KPI strip ─────────────────────────────────────────────────── */}
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col xs={12} md={6}>
              <Card>
                <Statistic
                  title="Total Runs"
                  value={summary.overall.total_runs}
                  prefix={<RiseOutlined />}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card>
                <Statistic
                  title="Total Bales Selected"
                  value={summary.overall.total_bales_selected}
                  suffix="bales"
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card>
                <Statistic
                  title="Avg Mic CV"
                  value={summary.overall.avg_mic_cv_pct ?? '—'}
                  suffix={summary.overall.avg_mic_cv_pct != null ? '%' : ''}
                  precision={2}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card>
                <Statistic
                  title="Avg SCI CV"
                  value={summary.overall.avg_sci_cv_pct ?? '—'}
                  suffix={summary.overall.avg_sci_cv_pct != null ? '%' : ''}
                  precision={2}
                />
              </Card>
            </Col>
          </Row>

          {/* ── Verdict + Drift distribution ────────────────────────────── */}
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col xs={24} md={14}>
              <Card title="Verdict mix" styles={{ body: { padding: 16 } }}>
                <Row gutter={12}>
                  <Col xs={8}>
                    <VerdictTile
                      verdict="GOOD"
                      count={summary.overall.verdict_distribution.GOOD}
                      total={summary.overall.total_runs}
                    />
                  </Col>
                  <Col xs={8}>
                    <VerdictTile
                      verdict="WARNING"
                      count={summary.overall.verdict_distribution.WARNING}
                      total={summary.overall.total_runs}
                    />
                  </Col>
                  <Col xs={8}>
                    <VerdictTile
                      verdict="BAD"
                      count={summary.overall.verdict_distribution.BAD}
                      total={summary.overall.total_runs}
                    />
                  </Col>
                </Row>
              </Card>
            </Col>
            <Col xs={24} md={10}>
              <Card title="Drift status" styles={{ body: { padding: 16 } }}>
                <Row gutter={[12, 12]}>
                  {Object.entries(summary.overall.drift_distribution).map(([k, v]) => (
                    <Col xs={12} key={k}>
                      <Card size="small" style={{ background: '#fafafa' }}>
                        <Statistic
                          title={k.replace(/_/g, ' ')}
                          value={v}
                          valueStyle={{
                            color: k === 'WITHIN_LIMITS' ? '#52c41a'
                              : k === 'EXCEEDS_LIMITS' ? '#ff4d4f'
                              : '#888',
                            fontSize: 22,
                          }}
                        />
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Card>
            </Col>
          </Row>

          {/* ── Per-recipe table ────────────────────────────────────────── */}
          <Card
            title="By recipe"
            style={{ marginTop: 16 }}
            styles={{ body: { padding: 0 } }}
          >
            {summary.by_recipe.length === 0 ? (
              <Empty
                description="No runs in this window"
                style={{ padding: 24 }}
              />
            ) : (
              <Table
                rowKey="recipe_id"
                size="small"
                dataSource={summary.by_recipe}
                pagination={false}
                columns={[
                  {
                    title: 'Recipe',
                    dataIndex: 'recipe_name',
                    render: (_, r) => (
                      <Space direction="vertical" size={0}>
                        <Text strong>{r.recipe_name}</Text>
                        {r.recipe_count_ne != null && (
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {r.recipe_count_ne}s Ne
                          </Text>
                        )}
                      </Space>
                    ),
                  },
                  {
                    title: 'Runs', dataIndex: 'runs_count', width: 80, align: 'center',
                    sorter: (a, b) => a.runs_count - b.runs_count,
                  },
                  {
                    title: 'Verdict mix', width: 180,
                    render: (_, r) => <VerdictMixBar dist={r.verdict_distribution} />,
                  },
                  {
                    title: 'Avg Mic CV%', dataIndex: 'avg_mic_cv_pct', width: 110, align: 'right',
                    render: (v) => v != null ? <Text>{v.toFixed(2)}%</Text> : <Text type="secondary">—</Text>,
                    sorter: (a, b) => (a.avg_mic_cv_pct ?? 99) - (b.avg_mic_cv_pct ?? 99),
                  },
                  {
                    title: 'Avg SCI CV%', dataIndex: 'avg_sci_cv_pct', width: 110, align: 'right',
                    render: (v) => v != null ? <Text>{v.toFixed(2)}%</Text> : <Text type="secondary">—</Text>,
                  },
                  {
                    title: 'Last run', dataIndex: 'last_run_at', width: 140,
                    render: (v) => v ? dayjs(v).format('DD MMM HH:mm') : '—',
                  },
                ]}
              />
            )}
          </Card>

          {/* ── Recent runs ─────────────────────────────────────────────── */}
          <Card
            title="Recent runs"
            style={{ marginTop: 16 }}
            styles={{ body: { padding: 0 } }}
          >
            {summary.recent_runs.length === 0 ? (
              <Empty
                description="No runs yet — try lowering the filter or running a Quick Laydown"
                style={{ padding: 24 }}
              />
            ) : (
              <Table
                rowKey="run_id"
                size="small"
                dataSource={summary.recent_runs}
                pagination={{ pageSize: 10, hideOnSinglePage: true }}
                columns={[
                  {
                    title: 'When', dataIndex: 'created_at', width: 140,
                    render: (v) => dayjs(v).format('DD MMM HH:mm'),
                  },
                  {
                    title: 'Verdict', width: 110, align: 'center',
                    render: (_, r) => (
                      <VerdictBadge band={r.within_band ?? null} size="pill" />
                    ),
                  },
                  {
                    title: 'Within band', dataIndex: 'within_band', width: 150,
                    render: (v) => v ?? <Text type="secondary">—</Text>,
                  },
                  {
                    title: 'Drift', dataIndex: 'drift_status', width: 140,
                    render: (v) => (
                      <Tag color={DRIFT_TAG_COLOR[v ?? 'UNKNOWN']}>
                        {v ?? 'UNKNOWN'}
                      </Tag>
                    ),
                  },
                  {
                    title: 'Bales',
                    width: 110, align: 'right',
                    render: (_, r) => (
                      <Text>
                        {r.bale_count} / {r.target_bales}
                      </Text>
                    ),
                  },
                  {
                    title: '', width: 80,
                    render: (_, r) => (
                      <Link to={`/bale-mgmt/laydown-planning?run_id=${r.run_id}`}>
                        Open
                      </Link>
                    ),
                  },
                ]}
              />
            )}
          </Card>
        </>
      )}
    </div>
  )
}
